import { IntlShape } from 'react-intl';

import { SELECTOR_MODE } from '~constants';
import { AvailableOption, Timeslot } from '~types/appsync.types';
import { Participant } from '~types/participant';

/**
 * Checks if a participant has a specific option selected
 * @param participant - The participant to check
 * @param optionSlug - The slug of the option to check
 * @returns true if the participant has the option
 */
export const participantHasOption = (participant: Participant, optionSlug: string): boolean => {
  return participant.options?.some((opt) => opt.slug === optionSlug) ?? false;
};

/**
 * Adds an option to a participant
 * @param participant - The participant to update
 * @param option - The option to add
 * @returns Updated participant
 */
export const addOptionToParticipant = (
  participant: Participant,
  option: AvailableOption,
): Participant => {
  const hasOption = participantHasOption(participant, option.slug);

  if (hasOption) {
    return participant;
  }

  return {
    ...participant,
    options: [...(participant.options || []), option],
  };
};

/**
 * Removes an option from a participant
 * @param participant - The participant to update
 * @param optionSlug - The slug of the option to remove
 * @returns Updated participant
 */
export const removeOptionFromParticipant = (
  participant: Participant,
  optionSlug: string,
): Participant => {
  return {
    ...participant,
    options: participant.options?.filter((opt) => opt.slug !== optionSlug) || [],
  };
};

export const addOptionToParticipants = (
  participants: Participant[],
  option: AvailableOption,
  participantIds: number[],
): Participant[] => {
  return participants.map((participant) => {
    const shouldHaveOption = participantIds.includes(participant.id);
    const hasOption = participantHasOption(participant, option.slug);

    if (shouldHaveOption && !hasOption) {
      return addOptionToParticipant(participant, option);
    }

    if (!shouldHaveOption && hasOption) {
      return removeOptionFromParticipant(participant, option.slug);
    }

    return participant;
  });
};

export const addOptionTimeslotToConcernedParticipants = (
  participants: Participant[],
  option: AvailableOption,
  timeslot?: Timeslot,
): Participant[] => {
  return participants.map((participant) => {
    const hasOption = participantHasOption(participant, option.slug);

    if (!hasOption || !timeslot) {
      return participant;
    }

    const updatedOptions = participant.options.map((opt) =>
      opt.slug === option.slug ? { ...opt, timeslot } : opt,
    );
    return { ...participant, options: updatedOptions };
  });
};

export const getParticipantsWithOption = (
  participants: Participant[],
  optionSlug: string,
): Participant[] => {
  return participants.filter((participant) => participantHasOption(participant, optionSlug));
};

export const getParticipantTitle = (
  participant: Participant,
  intl: IntlShape,
  participantMode: SELECTOR_MODE,
  allParticipants?: Participant[],
): { participantLabel: string; participantSubLabel: string } => {
  const isConsumerCategoryMode = participantMode === SELECTOR_MODE.CONSUMER_CATEGORY;

  if (isConsumerCategoryMode) {
    // Calculate index within the same consumer category
    let categoryIndex = participant.id;
    if (allParticipants) {
      const sameCategory = allParticipants
        .filter((p) => p.consumerCategory.shortname === participant.consumerCategory.shortname)
        .sort((a, b) => a.id - b.id);
      categoryIndex = sameCategory.findIndex((p) => p.id === participant.id) + 1;
    }

    return {
      participantLabel: participant.consumerCategory.name + ' ' + categoryIndex,
      participantSubLabel: `(${intl.formatMessage(
        {
          defaultMessage: '{ageMin}-{ageMax} years old',
          id: 'yearsRange',
        },
        {
          ageMax: participant.consumerCategory.ageMax,
          ageMin: participant.consumerCategory.ageMin,
        },
      )})`,
    };
  }

  return {
    participantLabel: `${intl.formatMessage({
      defaultMessage: 'Participant',
      id: 'participant',
    })} ${participant.id}`,
    participantSubLabel: `(${intl.formatMessage(
      {
        defaultMessage: '{count, plural, one {# year old} other {# years old}}',
        id: 'yearsNumber',
      },
      {
        count: participant.age,
      },
    )})`,
  };
};
